"use client";

import { updateLineItem } from "@lib/data/cart";
import { Text, toast } from "@medusajs/ui";
import ErrorMessage from "@modules/checkout/components/error-message";
import DeleteButton from "@modules/common/components/delete-button";
import LineItemOptions from "@modules/common/components/line-item-options";
import LineItemPrice from "@modules/common/components/line-item-price";
import LineItemUnitPrice from "@modules/common/components/line-item-unit-price";
import LocalizedClientLink from "@modules/common/components/localized-client-link";
import Spinner from "@modules/common/icons/spinner";
import Counter from "@modules/layout/components/counter";
import Thumbnail from "@modules/products/components/thumbnail";
import { useState } from "react";
import { ExtendedStoreCartLineItem } from "types/global";

type ItemProps = {
  item: ExtendedStoreCartLineItem;
  currencyCode: string;
};

const Item = ({ item, currencyCode }: ItemProps) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeQuantity = async (quantity: number) => {
    setError(null);
    setUpdating(true);

    await updateLineItem({
      lineId: item.id,
      quantity,
    })
      .then(() => {
        toast.info("Toast title", {
          description: "Toast body",
        });
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setUpdating(false);
      });
  };

  return (
    <div className="flex flex-row justify-between border-b border-gray-200 py-4" data-testid="product-row">
      <div className="flex flex-col lg:flex-row lg:gap-4 gap-1">
        <LocalizedClientLink href={`/products/${item.product_handle}`} className={"lg:w-28 w-20 lg:pt-1"}>
          <Thumbnail thumbnail={item.thumbnail} images={item.variant?.product?.images} size="square" />
        </LocalizedClientLink>

        <div className="flex-1 flex flex-col justify-between">
          <div className="pr-3">
            <LocalizedClientLink href={`/products/${item.product_handle}`} className="group">
              <Text
                className="txt-medium-plus text-[#434657] group-hover:text-primary-color"
                data-testid="product-title"
              >
                {item.product_title}
              </Text>
            </LocalizedClientLink>
            <LineItemOptions variant={item.variant} data-testid="product-variant" />
          </div>
          <div className="flex items-center gap-2">
            <Counter defaultValue={item.quantity} onChange={changeQuantity} error={error ?? undefined} />
            <DeleteButton id={item.id} data-testid="product-delete-button" />
            {updating && <Spinner />}
          </div>
          <ErrorMessage error={error} data-testid="product-error-message" />
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex gap-x-1">
          <Text className="text-ui-fg-muted">{item.quantity}x </Text>
          <LineItemUnitPrice item={item} style="tight" currencyCode={currencyCode} />
        </div>
        <LineItemPrice item={item} style="tight" currencyCode={currencyCode} />
      </div>
    </div>
  );
};

export default Item;
